import { Grid, Typography } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { LayoutBackground} from '../../components/Layout/LayoutBackground/LayoutBackground.component';
import { Prompt } from '../../components/Prompt/Prompt.component';
import { useHistory } from 'react-router-dom';
import { verifyUserToken } from '../../services/users/users.service';
import { useParams } from 'react-router-dom';
import { AuthUserEmail } from '../../components/UserProfile/AuthUserEmail.component';
import { AddUserPassword } from './ResetUserPassword.component';
import { TokenResponse } from '../EmailActivation/TokenResponse.types';
import useDocumentTitle from '../../hooks/DocumentTitle/documentTitle';
import { useTranslation } from 'react-i18next';

export const ConfirmResetPassword: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation();

  const [openAuthUserEmail, setOpenAuthUserEmail] = useState(false);

  const [isSuccessPromptVisible, setIsSuccessPromptVisible] = useState<boolean>(false);
  const [isErrorPromptVisible, setIsErrorPromptVisible] = useState<boolean>(false);
  const [isLinkExpiredPromptVisible, setIsLinkExpiredPromptVisible] = useState<boolean>(false);
  const [isLinkAlreadyUsedPromptVisible, setIsLinkAlreadyUsedPromptVisible] = useState<boolean>(false);
  const [isOpenAddNewPassword, setIsOpenAddNewPassword] = useState<boolean>(false);
  const [isSuccessConfirmPromptVisible, setIsSuccessConfirmPromptVisible] = useState<boolean>(false);

  const { token } = useParams() as { token: string };

  useDocumentTitle('Zmiana hasła');

  const SuccessErrorExpiredPromptSubText = () => {
    return (
      <Grid item xs={11}>
        <Typography
          variant="body1"
          style={{
            fontWeight: 400,
            fontSize: '15px',
            paddingBottom: '20px',
            lineHeight: 2,
          }}
        >
          Za chwilę automatycznie zostaniesz przekierowany/na do ekranu logowania. Jeśli przekierowanie nie nastąpi
          automatycznie kliknij w poniższy przycisk.
        </Typography>
      </Grid>
    );
  };

  const ConfirmPromptSubText = () => {
    return (
      <Grid item xs={11}>
        <Typography
          variant="body1"
          style={{
            fontWeight: 700,
            fontSize: '16px',
            paddingBottom: '20px',
            lineHeight: 2,
          }}
        >
          Jeżeli podany adres e-mail jest powiązany z Twoim kontem w {t('global:shortNames.system')}, zostanie wysłany link umożliwiający zmianę
          hasła
        </Typography>
      </Grid>
    );
  };

  const ErrorPromptSubText = () => {
    return (
      <Grid item xs={11}>
        <Typography
          variant="body1"
          style={{
            fontWeight: 700,
            fontSize: '15px',
            paddingBottom: '20px',
            lineHeight: 2,
          }}
        >
          Wysłanie linku aktywacyjnego nie powiodło się. Spróbuj ponownie później.
        </Typography>
      </Grid>
    );
  };

  const handleOpenAddNewPassword = () => {
    setIsSuccessConfirmPromptVisible(false);
    setIsOpenAddNewPassword(true);
  };

  const handleVerifyToken = () => {
    verifyUserToken(token).then(
      (res: TokenResponse) => {
        switch (res) {
          case TokenResponse.Valid:
            setIsSuccessConfirmPromptVisible(true);
            setTimeout(() => {
              handleOpenAddNewPassword();
            }, 5000);
            break;
          case TokenResponse.Expired:
            setIsLinkExpiredPromptVisible(true);
            break;
          case TokenResponse.NotExist:
            setIsLinkAlreadyUsedPromptVisible(true);
            break;
        }
      },
      () => {
        setIsErrorPromptVisible(true);
      },
    );
  };

  useEffect(() => {
    if (token !== undefined) {
      handleVerifyToken();
    } else setOpenAuthUserEmail(true);
  }, [token]);

  useEffect(() => {
    if (
      isSuccessPromptVisible ||
      isErrorPromptVisible ||
      isLinkExpiredPromptVisible ||
      isLinkAlreadyUsedPromptVisible
    ) {
      setTimeout(() => {
        history.push('/');
      }, 10000);
    }
  }, [isSuccessPromptVisible, isErrorPromptVisible, isLinkExpiredPromptVisible, isLinkAlreadyUsedPromptVisible]);

  useEffect(() => {
    if (isSuccessConfirmPromptVisible) {
      setTimeout(() => {
        handleOpenAddNewPassword();
      }, 10000);
    }
  }, [isSuccessConfirmPromptVisible]);

  const handleEmailConfirmed = () => {
    setOpenAuthUserEmail(false);
    setIsSuccessPromptVisible(true);
  };

  const logout = () => {
    history.push('/');
  };

  return (
    <>
      <LayoutBackground />
      <AuthUserEmail open={openAuthUserEmail}  userId={''} onEmailConfirmed={handleEmailConfirmed} />

      {isSuccessPromptVisible && (
        <Prompt
          type="info"
          open={isSuccessPromptVisible}
          isBold={true}
          size="xs"
          enhancedText={<ConfirmPromptSubText />}
          mainActionButtonText={'Powrót do logowania'}
          onConfirm={logout}
          isRemovedBackdrop={true}
        />
      )}
      {isErrorPromptVisible && (
        <Prompt
          type="error"
          open={isErrorPromptVisible}
          isBold={true}
          size="xs"
          enhancedText={<ErrorPromptSubText />}
          mainActionButtonText={'Powrót do logowania'}
          onConfirm={logout}
          isRemovedBackdrop={true}
        />
      )}
      {isLinkExpiredPromptVisible && (
        <Prompt
          type="expired link"
          open={isLinkExpiredPromptVisible}
          isBold={true}
          size="xs"
          mainText={'Twój link stracił ważność.'}
          enhancedText={<SuccessErrorExpiredPromptSubText />}
          mainActionButtonText={'Powrót do logowania'}
          onConfirm={logout}
          isRemovedBackdrop={true}
        />
      )}
      {isLinkAlreadyUsedPromptVisible && (
        <Prompt
          type="link already used"
          open={isLinkAlreadyUsedPromptVisible}
          isBold={true}
          size="xs"
          mainText={'Link aktywacyjny został już wykorzystany.'}
          enhancedText={<SuccessErrorExpiredPromptSubText />}
          mainActionButtonText={'Powrót do logowania'}
          onConfirm={logout}
          isRemovedBackdrop={true}
        />
      )}
      {isSuccessConfirmPromptVisible && (
        <Prompt
          type="success"
          open={isSuccessConfirmPromptVisible}
          isBold={true}
          size="xs"
          enhancedText={<SuccessErrorExpiredPromptSubText />}
          mainActionButtonText={`Ustaw nowe hasło w ${t('global:shortNames.system')}`}
          onConfirm={() => handleOpenAddNewPassword()}
          isRemovedBackdrop={true}
        />
      )}

      {isOpenAddNewPassword && <AddUserPassword token={token} open={isOpenAddNewPassword} user={undefined} />}
    </>
  );
};
